import { z } from "zod";

import { commentSchema } from "@lendflow/schemas";

import { fileSchema } from "@/api/http/schemas/fileSchemas";
import { offerDocumentSchema } from "@/api/http/schemas/documentsSchemas";
import { offerSchema } from "@/api/http/schemas/offerSchemas";
import { ownerSchema } from "@/api/http/schemas/ownerSchemas";

export const fundingAdvisorSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  telephone: z.string().nullable(),
  calendar_link: z.string().nullable(),
});

export type FundingAdvisor = z.infer<typeof fundingAdvisorSchema>;

export const stageBlockSchema = z.union([
  z.literal("all_offers"),
  z.literal("accepted_offer"),
  z.literal("funding"),
  z.literal("checkout"),
]);

export type StageBlock = z.infer<typeof stageBlockSchema>;

export const stageDataSchema = z.object({
  action_description: z.string().optional(),
  action_title: z.string().optional(),
  button_redirect_type: z
    .union([
      z.literal("resume_link"),
      z.literal("redirect_link"),
      z.literal("calendar_link"),
      z.null(),
    ])
    .optional(),
  button_text: z.string().nullable().optional(),
  button_url: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  is_current: z.boolean(),
  show_button: z.boolean().optional(),
  stage_description: z.string(),
  stage_name: z.string(),
  blocks: z.array(stageBlockSchema).optional(),
});

export type StageData = z.infer<typeof stageDataSchema>;

export enum OfferDisplay {
  Basic = 1,
  Dynamic,
}

const offerDisplaySchema = z.nativeEnum(OfferDisplay);

export type Owner = z.infer<typeof ownerSchema>;

export const chatSettingsSchema = z.object({
  is_enabled: z.boolean().default(false),
  agent_id: z.string().nullable().optional(),
  agent_webhook_url: z.string().nullable().optional(),
});

export type ChatSettings = z.infer<typeof chatSettingsSchema>;

const offerTemplateSettingSchema = z.object({
  id: z.string(),
  weight: z.number(),
});

const funderSettingSchema = z.object({
  id: z.string(),
  offer_templates: z.array(offerTemplateSettingSchema),
});

export const applicationSchema = z.object({
  id: z.string(),
  amount_needed: z.number().nullable(),
  personal_information_id: z.number(),
  business_legal_name: z.string(),
  funding_advisor: fundingAdvisorSchema.nullable(),
  workflow_template_id: z.string(),
  stages: z
    .array(stageDataSchema)
    .nullable()
    .transform((val) => val ?? []),
  created_at: z.string().datetime(),
  offer_declined_reasons: z.array(z.string()),
  offer_declined_reasons_other_label: z.string().nullable(),
  offers: z.array(offerSchema),
  owners: z.array(ownerSchema),
  is_dead: z.boolean(),
  offer_display: offerDisplaySchema,
  is_finished: z.boolean(),
  is_checkout_enabled: z.boolean(),
  default_borrower_platform_template_id: z.string().nullable(),
  required_stips: z.array(offerDocumentSchema),
  chat: chatSettingsSchema.nullable().optional(),
  funder_setting: z.array(funderSettingSchema),
  comments: z.array(commentSchema).optional(),
});

export type Application = z.infer<typeof applicationSchema>;

const applicationListItemSchema = z.object({
  id: z.string(),
  business_legal_name: z.string().nullable(),
  workflow_template_id: z.string(),
  created_at: z.string().datetime(),
});

export type ApplicationListItem = z.infer<typeof applicationListItemSchema>;

export const allApplicationsSchema = z.array(applicationListItemSchema);

export const resumeLinkSchema = z.object({
  url: z.string().url(),
});

export type ResumeLink = z.infer<typeof resumeLinkSchema>;

const consentSchema = z.object({
  id: z.string().uuid(),
  type: z.enum([
    "third_party_data_sharing",
    "application_data_accuracy",
    "credit_check",
    "communication",
  ]),
  is_required: z.boolean(),
  message: z.string(),
});

const consentContentSchema = z.object({
  version: z.string(),
  message: z.string(),
  hide_text_in_scroll_area: z.boolean(),
  consents: z.array(consentSchema).nullable(),
});

export const consentTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  client_id: z.string().nullable(),
  is_default: z.boolean(),
  is_lendflow_template: z.boolean(),
  content: consentContentSchema,
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type ConsentTemplate = z.infer<typeof consentTemplateSchema>;
export type Consent = z.infer<typeof consentSchema>;

const signatureConsentSchema = z.object({
  id: z.string().uuid(),
  checked_date: z.string().datetime().nullable(),
});

export enum SignatureStatus {
  "Requested" = 1,
  "Signed",
  "Failed",
}

const signedDocumentRecipient = z.object({
  id: z.string(),
  role: z.enum(["signer", "client", "guarantor"]),
  entity: z.number(),
});

const signedDocumentSchema = z.object({
  id: z.number(),
  personal_information_id: z.number(),
  signature_status: z.nativeEnum(SignatureStatus),
  last_recipient: z.number().nullable(),
  recipients: z.array(signedDocumentRecipient),
  type: z.enum(["application", "checkout"]),
  source: z.enum(["docusign", "consent", "file_upload"]),
  consents: z.array(signatureConsentSchema),
  templateable_type: z.string(),
  templateable_id: z.string(),
  files: z.array(fileSchema),
});

export const signedDocumentsSchema = z.array(signedDocumentSchema);

export type SignedDocument = z.infer<typeof signedDocumentSchema>;
export type SignedDocumentConsent = z.infer<typeof signatureConsentSchema>;

export interface EnrichApplicationOptions {
  code?: string;
}
